import {Injectable} from "@angular/core";
import {GridLayoutService} from "@irb-common/interfaces/grid-layout.service";
import {DeleteCellComponent} from "@irb-common/components/grid/renderers/delete.cell.component";
import {DeleteHeaderComponent} from "@irb-common/components/grid/renderers/delete.header.component";
import {TruncateTooltipCellComponent} from '@irb-common/components/grid/renderers/truncatetooltip.cell.component';
import {IrbEditorsEvents} from "@irb/irb-common/events";
import {RebalanceIncludedPortfolioComponent} from "./rebalance-included-portfolio.component";
import {AppService} from "@irb/app.service";
import {lightningIconCellComponent} from "./delete-conditional.cell.component";
import {customCheckboxCellRenderer} from "./customCheckBoxCellRenderer.component";
import {NumberTextBoxRendererComponent} from "@irb-common/components/grid/renderers/number-text-box.cell.component";
import { IDropdownItem } from '@irb/irb-common/models/dropdown.model';
import { DropdownFilterRendererComponent } from '@irb/irb-common/components/grid/renderers/dropdown-filter.cell.component';

@Injectable({
    providedIn: "root"
})
export class IncludedPortfoliosGridLayoutService implements GridLayoutService<RebalanceIncludedPortfolioComponent> {
    sizeFor: any;
    lossPriorityValues: Array<IDropdownItem> = [
        {name: "Use Short Term losses only to meet the target", value: "Use Short Term losses only to meet the target"},
        {name: "Use Long Term losses only to meet the target", value: "Use Long Term losses only to meet the target"},
        {name: "Use Short, then long term losses to meet the target", value: "Use Short, then long term losses to meet the target"},
        {name: "Use all losses, prioritizing from the largest $ loss first to minimize trades", value: "Use all losses, prioritizing from the largest $ loss first to minimize trades"}
    ];

    mapAnalysisAccountDtoList: Map<string, any[]> = new Map<string, any[]>();
    constructor(private as: AppService
    ) {
        this.sizeFor = {
            "accountNumber": 400,
            "accountName": 120,
            "accountsubType": 200,
            "marketValue": 200,
            "accountLocationType": 200,
            "masterNumber": 200,
            "smaSymbolName": 100,
            "deletecol": 25,
            "portName": 920,
            "status": 337

        };
    }

    getFrameworkComponents() {
        return {
            "DeleteHeaderComponent": DeleteHeaderComponent,
            "DeleteCellComponent": DeleteCellComponent,
            "TruncateTooltipCellComponent": TruncateTooltipCellComponent,
            "lightningIconCellComponent": lightningIconCellComponent,
            "customCheckboxCellRenderer": customCheckboxCellRenderer,
            'NumberTextBoxRendererComponent': NumberTextBoxRendererComponent,
            "DropdownFilterRendererComponent": DropdownFilterRendererComponent,
        };
    }
    getlosspriorityLabel(params: any) {
        if (!params.data.htlAmount) {
            return "--";
        }
        else {
            return params.data.htlAmount;
        }

    }
    extraCashColInfo() {
        return `
          <p>
          Specify any extra cash amounts for accounts in portfolios by expanding the row and entering an amount. Click the button above to import a list of accounts with extra cash amounts (their portfolios will be automatically added to this rebalance).
          </p> `
    }
    liqdColInfo() {
        return `
          <p>
          Selecting "liquidate" tells iRebal to propose selling all holdings in this account during this rebalance, ignoring any rules or restrictions that would typically prevent sell recommendations, potentially resulting in penalty fees. <br>
          <br>
          The value of this account will be excluded from the portfolio value during this rebalance, leaving only the holdings in other accounts in the portfolio to be considered. <br>
          <br>
          This setting is only applicable for one rebalance submission and the cash generated by liquidating the account may be reinvested during subsequent rebalances, if the cash is not withdrawn or the account closed.
          </p> `
    }
    showLossPrioritySettingsDialog(params: any) {
        if (params.value === "--") {
            this.as.fireEvent(IrbEditorsEvents.SHOW_LOSS_PRIORITY_SETTINGS_DIALOG, { data: params.data });
        }
    }

    getColumnDefs(parent: RebalanceIncludedPortfolioComponent) {
        return [{
            headerName: "Delete All",
            headerComponent: "DeleteHeaderComponent",
            headerComponentParams: {
                message: "Remove all items?",
                note: "",
                proceed: parent.deleteAll.bind(parent),
                cancel: parent.cancelDelete.bind(parent)
            },
            cellRendererSelector: (params: any) => {
                const lightningicon = {
                    component: 'lightningIconCellComponent'
                }
                const deleteCellComponent = {
                    component: 'DeleteCellComponent',
                    params: {
                        message: "Remove this item?",
                        note: "",
                        proceed: parent.delete.bind(parent),
                        cancel: parent.cancelDelete.bind(parent)
                    }
                };
                if (params && params.node && params.node.level === 0) {
                    if (params && params.data && params.data.isSmartFiltered)
                        return lightningicon;
                    else
                        return deleteCellComponent;
                }
            },

            resizable: false,
            minWidth: 25,
            maxWidth: 25,
            width: 25,
            cellStyle: (params: any) => {
                if (params && params.data && params.data.isSmartFiltered) {
                    return { 'text-align': 'center', 'padding-left': '2px' };
                }
                return null;
            }
        },
        {
            headerName: "Portfolios",
            marryChildren:true,
            children: [
                { field: "portfolioNameGroup", rowGroup: true, hide: true },
                {
                    headerName: "Name",
                    field: "portfolioName",
                    cellRenderer : "cellRendererFormatter",
                    minWidth: 350,
                    flex: 2,
                    suppressFiltersToolPanel: true,
                    sort: "asc",
                    suppressColumnsToolPanel: true,
                    cellClassRules: {
                        'ag-grid-no-children-chevron-spacer': (params: any) => {
                            return params && params.data && params.data.acctQty <= 0;
                        }
                    },
                },
                {
                    headerName: "Status",
                    field: "status",
                    minWidth: 100,
                    flex: 1,
                    cellRendererSelector: (params: any) => {
                        let finalValue = '';
                        if (params.data && params.data.status) {
                            finalValue = params.data.status;
                            if (finalValue && finalValue.toLowerCase() === 'complete') {
                                finalValue = '';
                            }
                        }
                        return {
                            component: 'cellRendererFormatter',
                            params: { value: finalValue }
                        };
                    },
                    cellStyle: function (params: any) {
                        if (params.value && params.value.toLowerCase() === 'incomplete') {
                            return { color: 'red' }
                        } else {
                            return null;
                        }
                    }
                }
            ]
        },
        {
            headerName: "Accounts",
            marryChildren:true,
            children: [
                {
                    showRowGroup: true,
                    cellRenderer: 'agGroupCellRenderer',
                    cellRendererParams: {
                        suppressCount: true,
                    },
                    headerName: "Acct#",
                    field: "accountNumber",
                    minWidth: 130,
                    flex: 1,
                    hide: !parent.hideImportExtraCashAndColDefs,
                    valueGetter: (params: any) => {
                        let retVal: string = params.node.expanded ? 'Hide Accts' : 'Show Accts';

                        if (params.node && params.node.data) {
                            if (params.node.data.acctQty === 0)
                                retVal = '';
                            if (params.node.data.accountNumber)
                                retVal = params.node.data.accountNumber;
                        }
                        return retVal;
                    },
                    cellStyle: function (params: any) {
                        if (params.value && (params.value === 'Hide Accts' || params.value === 'Show Accts')) {
                            return { 'font-style': 'italic' }
                        } else {
                            return null;
                        }
                    }
                },
                {
                    headerName: "Account Name",
                    field: "accountName",
                    cellRenderer : "cellRendererFormatter",
                    minWidth: 350,
                    flex: 2,
                    sortable:false,
                    hide: !parent.hideImportExtraCashAndColDefs
                },
                {
                    headerName: "Type",
                    field: "accountType",
                    cellRenderer : "cellRendererFormatter",
                    minWidth: 100,
                    flex: 1,
                    hide: !parent.hideImportExtraCashAndColDefs
                }
            ]
        },
        {
            headerName: "Extra Cash",
            headerGroupComponent: 'customHeaderGroup',
            hide: !parent.hideImportExtraCashAndColDefs,
            headerGroupComponentParams: {
                irbColInfo: this.extraCashColInfo()
            },
            children: [
                {
                    headerName: "Amt",
                    minWidth: 120,
                    flex: 1,
                    hide: !parent.hideImportExtraCashAndColDefs,
                    aggFunc: (params: any) => {
                        if (params && params.rowNode.group === false) {
                            let total: number = 0;
                            params.values.forEach(function (value: any) {
                                if (Number(value)) {
                                    total += Number(value);
                                }
                            });
                            params.data.extraCashAmount = total;
                            return total;
                        }
                    },
                    field: "extraCashAmount",
                    cellStyle: (params: any) => {
                        if (params && params.node && params.node.level === 1 && params.data && params.data.isLiquidate) {
                            return { color: '#D30014' };
                        } else if (params && params.node && params.node.level === 0 && params.data) {
                            const isLiquidate = params.node.childrenAfterGroup.filter((entry: any) => (entry.data.isLiquidate === true));
                            return (isLiquidate && isLiquidate.length) ? { color: '#D30014' } : { color: '#000000' };
                        } else {
                            return { color: '#000000' };
                        }
                    },
                    cellClassRules: {
                        'numberTextBoxCell': (params: any) => {
                            if (params && params.node && params.node.level === 1
                                && params.data && !this.canEdit(params)) { return true; }
                        }
                    },
                    cellRendererSelector: (params: any) => {
                        if(params && params.node){
                            const notAllowedEdit:boolean = this.canEdit(params);
                        if (params.node.level === 0 || (params.node.level === 1 && params.data && notAllowedEdit)) {
                            return {
                                component: 'cellRendererFormatter',
                                params: { type: 'currency', decimalPosition: '2',
                                            isNotApplicable: (params.node.level === 1)?(notAllowedEdit && !params.data.isLiquidate):false,
                                            isDoubleDash:!(params.value)
                                        }
                            };
                        } else if (params.node.level === 1) {
                            return {
                                component: "NumberTextBoxRendererComponent",
                                params: {
                                    min: 0,
                                    max: 999999999,
                                    places: 2,
                                    isCurrency: true,
                                    isNotApplicable:notAllowedEdit,
                                    invalidMessage: 'Invalid input',
                                    invalidRangeMessage: 'Invalid range',
                                    disabled: notAllowedEdit,
                                    addZeroIfDotFirst:true,
                                    autofocus: params.node.parent && params.node.parent.data && params.node.parent.data.acctQty === 1,
                                    onChange: (params: any, c: any) => {
                                        const currentValue = Number(c.value);
                                        if (params.data.extraCashAmount != currentValue && currentValue && params.node.group === false && c.isValid) {
                                            params.node.setDataValue('extraCashAmount', currentValue);
                                        }if(currentValue === 0){
                                            params.node.setDataValue('extraCashAmount', currentValue);
                                        }
                                    },
                                }
                            }
                        }
                        }      
                        else {
                            return '';
                        }
                    },
                    /*tooltip: (params: any) => {
                        if (params.data.accountType === 'EXTERNAL' || params.data.isSMA) {
                            return "Extra cash cannot be entered for external and sma accounts";
                        }
                    }*/
                },
            ]
        },
        {
            headerName: "Liquidate",
            headerGroupComponent: 'customHeaderGroup',
            hide: !parent.hideImportExtraCashAndColDefs,
            headerGroupComponentParams: {
                irbColInfo: this.liqdColInfo()
            },
            children: [
                {
                    headerName: "Full",
                    hide: !parent.hideImportExtraCashAndColDefs,
                    field: "isLiquidate",
                    minWidth: 100,
                    flex: 1,
                    cellStyle: { 'text-align': 'center' },
                    aggFunc: (params: any) => {
                        const isLiquidate = params.values.filter((entry: any) => (entry === true));
                        return (isLiquidate && isLiquidate.length) ? true : false;
                    },
                    cellClassRules: {
                        'alertIcon': (params: any) => {
                            if (params && params.node && params.node.level === 0) {
                                return params.value;
                            }
                        }
                    },
                    cellRendererSelector: (params: any) => {
                        if (params && params.node && params.node.level === 0) {
                            return {
                                component: 'cellRendererFormatter',
                                params: { value: ' ' }
                            };
                        } else if (params && params.node && params.node.level === 1 && (params.data.accountType === 'EXTERNAL' || params.data.isSMA)) {
                            return {
                                component: "cellRendererFormatter",
                                params: { value: 'n/a' }
                            }
                        } else if (params && params.node && params.node.level === 1) {
                            return {
                                component: "customCheckboxCellRenderer",
                                params: { parent: parent }
                            }
                        }
                        else {
                            return '';
                        }
                    }
                }
            ]
        },
        ];
    }

    getColumnDefsPortFStatus(parent: RebalanceIncludedPortfolioComponent) {
        return [
            {
                headerName: "Delete All",
                headerComponent: "DeleteHeaderComponent",
                headerComponentParams: {
                    message: "Remove all items?",
                    note: "",
                    proceed: parent.deleteAll.bind(parent),
                    cancel: parent.cancelDelete.bind(parent)
                },
                cellRendererSelector: (params: any) => {
                    const lightningicon = {
                        component: 'lightningIconCellComponent'
                    };
                    const deleteCellComponent = {
                        component: 'DeleteCellComponent',
                        params: {
                            message: "Remove this item?",
                            note: "",
                            proceed: parent.delete.bind(parent),
                            cancel: parent.cancelDelete.bind(parent)
                        }
                    };
                    if (params && params.node && params.node.level === 0) {
                        if (params && params.data && params.data.isSmartFiltered)
                            return lightningicon;
                        else
                            return deleteCellComponent;
                    }
                },

                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25,
                cellStyle: (params: any) => {
                    if (params && params.data && params.data.isSmartFiltered) {
                        return {'text-align': 'center', 'padding-left': '2px'};
                    }
                    return null;
                }
            },
            {
                headerName: "Portfolios",
                children: [
                    {field: "portfolioNameGroup", rowGroup: true, hide: true},
                    {
                        headerName: "Name",
                        field: "portfolioName",
                        cellRenderer : "cellRendererFormatter",
                        minWidth: 300,
                        flex:1,
                        suppressFiltersToolPanel: true,
                        sort: "asc",
                        suppressColumnsToolPanel: true,
                        cellClassRules: {
                            'ag-grid-no-children-chevron-spacer': (params: any) => {
                                return params && params.data && params.data.acctQty <= 0;
                            }
                        },
                    },
                    {
                        headerName: "Status",
                        field: "status",
                        minWidth: 300,
                        flex:1,
                        cellRendererSelector: (params: any) => {
                            let finalValue = '';
                            if (params.data && params.data.status) {
                                finalValue = params.data.status;
                                if (finalValue && finalValue.toLowerCase() === 'complete') {
                                    finalValue = '';
                                }
                            }
                            return {
                                component: 'cellRendererFormatter',
                                params: {value: finalValue}
                            };
                        },
                        cellStyle: function (params: any) {
                            if (params.value && params.value.toLowerCase() === 'incomplete') {
                                return {color: 'red'}
                            } else {
                                return null;
                            }
                        }
                    }
                ]
            }
        ]
    }

    getHarvestTaxLossColoumnDefs(parent: RebalanceIncludedPortfolioComponent) {
        return [
            {
                headerName: "Delete All",
                headerComponent: "DeleteHeaderComponent",
                headerComponentParams: {
                    message: "Remove all items?",
                    note: "",
                    proceed: parent.deleteAll.bind(parent),
                    cancel: parent.cancelDelete.bind(parent)
                },
                cellRendererSelector: function (params: any) {
                    var lightningicon = {
                        component: 'lightningIconCellComponent'
                    }

                    var deleteCellComponent = {
                        component: 'DeleteCellComponent',
                        params: {
                            message: "Remove this item?",
                            note: "",
                            proceed: parent.delete.bind(parent),
                            cancel: parent.cancelDelete.bind(parent)
                        }
                    };

                    if (params && params.data && params.data.isSmartFiltered)
                        return lightningicon;
                    else
                        return deleteCellComponent;
                },

                resizable: false,
                minWidth: 25,
                maxWidth: 25,
                width: 25
            },
            {
                headerName: "Portfolios",
                children: [
                    {
                        headerName: "Name",
                        field: "portfolioName",
                        cellRenderer : "cellRendererFormatter",
                        minWidth: this.sizeFor.accountNumber,
                        flex: 2,
                        sort: "asc",
                    },
                    {
                        headerName: "Status",
                        field: "status",
                        minWidth: this.sizeFor.accountName,
                        maxWidth: this.sizeFor.accountName,
                        flex: 1,
                        cellRendererSelector: (params: any) => {
                            let finalValue = '';
                            if (params.data && params.data.status) {
                                finalValue = params.data.status;
                            }
                            if (finalValue && finalValue.toLowerCase() === 'complete') {
                                finalValue = '';
                            }
                            return {
                                component: 'cellRendererFormatter',
                                params: { value: finalValue }
                            };
                        }
                    },
                ]
            },
            {
                headerName: "Loss Target & Priority",
                headerGroupComponent: 'customHeaderGroup',
                headerGroupComponentParams: {
                    irbColInfo: this.lossTargetPriorityInfo()
                },
                children: [

                    {
                        headerName: "Amount",
                        field: "htlAmount",
                        minWidth: 200,
                        maxWidth: 200,
                        width: this.sizeFor.isGlobal,
                        cellRenderer: 'cellRendererFormatter',
                        valueGetter: this.getlosspriorityLabel,
                        onCellClicked: this.showLossPrioritySettingsDialog.bind(this),
                        flex: 1
                    },
                    {
                        headerName: 'Loss Priority',
                        field: 'lossPrty',
                        suppressFiltersToolPanel: true,
                        filter: 'agSetColumnFilter',
                        //cellClass: ['my-class1'],
                        filterParams: {
                            buttons: ['reset', 'apply'],
                            newRowsAction: 'keep',
                            suppressSorting: true,
                            closeOnApply: true,
                            cellHeight: 30
                        },
                        flex: 2,
                        cellClass: 'dropdownFilterCell',
                        cellRenderer: 'DropdownFilterRendererComponent',
                        cellRendererParams: (params: any) => {
                            return {
                                defaultValue: {name: params.data.lossPrty, value: params.data.lossPrty},
                                values: this.lossPriorityValues,
                                placeholder: 'none',
                                disabled: (params.data.lossPrty) ? false : true,
                                onChange: (_params: any, _item: IDropdownItem) => {
                                    _params.data.lossPrty = _item.value;
                                    parent.dataChange.emit(true);
                                }
                            }
                        }
                    },
                ]
            },
        ];
    }
    lossTargetPriorityInfo() {
        let msg = `
      <div style='width:450px; margin:2px;'>
        You may specify a target loss dollar amount for each portfolio. If no option is selected here,
        iRebal will simply harvest all losses that meet the $ and % thresholds. If you enter a target,
        iRebal will stop proposing sales when either the estimated realized loss amount meets or exceeds
        the target, or when all sales matching the loss priority settings are exhausted. When specifying
        a target loss amount, you must also tell iRebal in which sequence to sell for losses. The available choices are:

        <ul>
            <li>Only use long-term losses to meet the loss target.</li>
            <li>Only use short-term losses to meet the loss target.</li>
            <li>Use short-term losses first, followed by long-term to meet the target.</li>
            <li>Minimize trades by selling the largest loss first.</li>
        </ul>
    </div>
    `;
        return msg;
    }
    canEdit(params: any) {
        return (
            params && params.node && (params.node.level === 0 ||
                params.data.accountType === 'EXTERNAL' || params.data.isSMA || params.data.isLiquidate));
    }
}
